import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';

import { ColorUtils } from '@ppg/shared/color/services';

@Component({
  standalone: true,
  selector: 'ppg-background-color',
  templateUrl: 'background-color.component.html',
  styleUrl: 'background-color.component.scss',
  imports: [NgOptimizedImage],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BackgroundColorComponent {
  readonly element = inject(ElementRef);
  readonly #colorUtils = inject(ColorUtils);

  readonly imageUrl = input<string>();
  readonly rgb = input<string>();
  readonly alt = input<string>('');
  readonly fallbackColor = input<string>();
  readonly onImageLoaded = output();
  readonly onImageLoadError = output();

  readonly #failedImageUrl = signal<string | null>(null);

  protected readonly renderMode = computed(() => {
    const imageUrl = this.imageUrl();
    const failedImageUrl = this.#failedImageUrl();
    if (!!imageUrl && failedImageUrl !== imageUrl) {
      return 'image';
    }

    return 'rgb';
  });

  readonly #hexColor = computed(() =>
    this.rgb() ? `${this.#colorUtils.rgbToHex(this.#colorUtils.stringToRgb(this.rgb()))}` : '',
  );

  protected readonly bgColor = computed(() => this.#hexColor() || this.fallbackColor() || '');

  protected imageLoadFailed(): void {
    this.#failedImageUrl.set(this.imageUrl()!);
    this.onImageLoadError.emit();
  }

  protected imageLoaded(): void {
    this.#failedImageUrl.set(null);
    this.onImageLoaded.emit();
  }
}
